import { Component, OnChanges, OnInit, inject } from '@angular/core';
import { InspectionResultMatrixDto } from '../dtos/inspectionResultMatrixDto';
import { InspectionResultMatrixColumnDto } from '../dtos/inspectionResultMatrixColumnDto';
import { InspectionResultMatrixRowDto } from '../dtos/inspectionResultMatrixRowDto';
import { InspectionResultPointDto } from '../dtos/inspectionResultPointDto';
import { InspectionResultSummaryDto } from '../dtos/inspectionResultSummaryDto';
import { InspectionResultMatrixRowPointDto } from '../dtos/inspectionResultMatrixRowPointDto';
import { InspectionResultMatrixPointGroupDto } from '../dtos/inspectionResultMatrixPointGroupDto';
import { DetailBaseComponent } from './detail-base.component';
import { I18nTextPipe } from '../core/pipes/i18n-text.pipe';
import { FlatInspectionResultDto } from '../core/api/model/flatInspectionResultDto';
import { I18NText } from '../core/api/model/i18NText';
import { InspectionElementDto } from '../core/api/model/inspectionElementDto';
import { InspectionResultType } from '../core/api/model/inspectionResultType';
import { InspectionResultElementType } from '../core/api/model/inspectionResultElementType';
import { PointGroupType } from '../core/api/model/pointGroupType';
import { InspectionElementFieldType } from '../core/api/model/inspectionElementFieldType';
import { NgClass } from '@angular/common';
import { NgbPopover, NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { DetailResultDropdownComponent } from './detail-result-dropdown.component';
import { DetailResultButtonComponent } from './detail-result-button.component';
import { DetailResultFieldValueComponent } from './detail-result-field-value.component';
import { DetailResultYesNoButtonComponent } from './detail-result-yes-no-button.component';
import { DetailResultNumberFieldValueComponent } from './detail-result-number-field-value.component';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DetailCategoryResultComponent } from './detail-category-result.component';
import { MkdeAlertHolderDirective } from '../alert/mkde-alert-holder.directive';
import { keysToTranslate } from '@core/util/keys-to-translate';

@Component({
  selector: 'app-detail-matrix',
  templateUrl: './detail-matrix.component.html',
  styleUrls: ['./detail-matrix.component.scss', './detail.component.scss'],
  imports: [
    NgClass,
    NgbPopover,
    DetailResultDropdownComponent,
    NgbCollapse,
    DetailResultButtonComponent,
    DetailResultFieldValueComponent,
    DetailResultYesNoButtonComponent,
    DetailResultNumberFieldValueComponent,
    TranslateDirective,
    DetailCategoryResultComponent,
    TranslatePipe,
    I18nTextPipe,
  ],
})
export class DetailMatrixComponent extends DetailBaseComponent implements OnInit, OnChanges {
  private translateService = inject(TranslateService);
  private alerts = inject(MkdeAlertHolderDirective);

  public matrix?: InspectionResultMatrixDto;

  public notApplicablePointGroupsDisplayed = false;

  public fieldValueTypes = InspectionElementFieldType;

  public constructor() {
    keysToTranslate(['MatrixNotAvailable']);
    super();
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.notApplicablePointGroupsDisplayed = this.notApplicableCategoriesDisplayed;
    this.buildMatrix();
  }

  public ngOnChanges(changes: any): void {
    if (
      changes.notApplicableCategoriesDisplayed &&
      changes.notApplicableCategoriesDisplayed.currentValue
    ) {
      this.notApplicablePointGroupsDisplayed = true;
    }
  }

  public categoryResultChanged(propagate: boolean) {
    const result = this.getResult(this.inspectionCategory.key);
    if (propagate && result) {
      this.propagateResultToChildren(this.inspectionCategory, result.inspectionResult);
      this.resetAllRowResultModels();
      this.resetAllPointGroupResultModels();
    }
    this.save.emit();
    this.handleDefects();
  }

  public columnResultChanged(column: InspectionResultMatrixColumnDto) {
    const pointGroup = this.getInspectionElement(
      this.inspectionCategory,
      column.pointGroupResult?.inspectionElementKey,
    );
    this.propagateResultToChildren(pointGroup, column.pointGroupResult?.inspectionResult, true);
    this.resetCategoryResult(column.pointGroupResult);
    this.resetAllRowResultModels();
    this.resetAllPointGroupResultModels();
    this.handleDefects();
    this.save.emit();
  }

  public rowResultChanged(row: InspectionResultMatrixRowDto) {
    this.propagateResultsToRowPoints(row);
    this.resetCategoryResult(row.pointResultModel);
    this.resetAllPointGroupResultModels();
    this.handleDefects();
    this.save.emit();
  }

  public rowPointGroupResultChanged(pointGroup: InspectionResultMatrixPointGroupDto) {
    const pointGroupElement = this.getPointGroupElement(pointGroup);
    this.propagateResultToChildren(
      pointGroupElement,
      pointGroup.pointGroupResult?.inspectionResult,
      true,
    );
    this.resetCategoryResult(pointGroup.pointGroupResult);
    this.resetAllRowResultModels();
    this.handleDefects();
    this.save.emit();
  }

  public rowPointResultChanged(rowPoint: InspectionResultMatrixRowPointDto, pointNumber: string) {
    this.resetPointGroupResult(rowPoint.pointGroupKey, rowPoint.result);
    this.resetCategoryResult(rowPoint.result);
    this.resetRowResultModel(pointNumber, rowPoint.result);
    this.handleDefects();
    this.save.emit();
  }

  public rowPointResultFieldValueChanged(rowPoint: InspectionResultMatrixRowPointDto) {
    this.resetPointGroupResult(rowPoint.pointGroupKey, rowPoint.result);
    this.resetCategoryResult(rowPoint.result);
    this.handleDefects();
    this.save.emit();
  }

  public pointResultChanged(point: FlatInspectionResultDto) {
    this.resetCategoryResult(point);
    this.handleDefects();
    this.save.emit();
  }

  public pointResultFieldValueChanged() {
    this.handleDefects();
    this.save.emit();
  }

  public getColumnsToDisplay(): InspectionResultMatrixColumnDto[] {
    return (
      this.matrix?.columns?.filter(
        (c) =>
          this.notApplicablePointGroupsDisplayed ||
          c.pointGroupResult?.inspectionResult !== InspectionResultType.NotApplicable,
      ) ?? []
    );
  }

  public getPointsToDisplay(
    row: InspectionResultMatrixRowDto,
  ): InspectionResultMatrixRowPointDto[] {
    const columnsToDisplay = this.getColumnsToDisplay();
    return (
      row.points?.filter(
        (p) =>
          this.notApplicablePointGroupsDisplayed ||
          columnsToDisplay.some(
            (c) => c.pointGroupResult?.inspectionElementKey === p.pointGroupKey,
          ),
      ) ?? []
    );
  }

  public getDependingResultsForColumn(
    column: InspectionResultMatrixColumnDto,
  ): FlatInspectionResultDto[] {
    const pointGroup = this.getInspectionElement(
      this.inspectionCategory,
      column.pointGroupResult?.inspectionElementKey,
    );
    const points = this.getAllPoints(pointGroup, null);
    return points.map((point) => this.getResult(point.key)).filter((result) => result != null);
  }

  public getDependingResultsForRow(
    row: InspectionResultMatrixRowDto,
  ): (FlatInspectionResultDto | null | undefined)[] {
    return this.getPointsToDisplay(row)
      .filter((point) => point.result)
      .map((point) => point.result);
  }

  public getPointGroupsOfRow(
    row: InspectionResultMatrixRowDto,
  ): InspectionResultMatrixPointGroupDto[] {
    const displayedColumns = this.getColumnsToDisplay();
    return (
      this.matrix?.pointGroups?.filter(
        (a) =>
          a.rowNumber === row.number &&
          displayedColumns.find((c) => c.pointGroupNumber === a.columnNumber),
      ) ?? []
    );
  }

  public getDependingResultsForPointGroup(
    pointGroup: InspectionResultMatrixPointGroupDto,
  ): FlatInspectionResultDto[] {
    const pointGroupElement = this.getPointGroupElement(pointGroup);
    const points = this.getAllPoints(pointGroupElement, null);
    return points.map((point) => this.getResult(point.key)).filter((result) => result != null);
  }

  public displayRowPointTemplateColumn(): boolean {
    return (
      (this.getColumnsToDisplay().length > 1 &&
        this.matrix?.rows?.some((row) => !row.isFieldValuePoint)) ??
      false
    );
  }

  public getPointsOnCategoryLevelColSpan(): number {
    return Math.max(
      1,
      this.getColumnsToDisplay().length + (this.displayRowPointTemplateColumn() ? 1 : 0),
    );
  }

  private buildMatrix(): void {
    const pointsOnCategoryLevel =
      this.inspectionCategory.childElements?.filter(
        (childElement) => childElement.type === InspectionResultElementType.Point,
      ) ?? [];
    const type0PointGroups = this.inspectionCategory.childElements?.filter(
      (childElement) =>
        childElement.type === InspectionResultElementType.PointGroup &&
        childElement.pointGroupType === PointGroupType.Regular,
    );
    if (!type0PointGroups || type0PointGroups.length === 0) {
      this.displayDangerAlertMatrixNotAvailable();
      return;
    }
    const matrixRows = this.getMatrixRows(type0PointGroups);
    const matrixColumns = this.getMatrixColumns(type0PointGroups);
    const matrixPointGroups = this.getMatrixPointGroups(type0PointGroups);
    this.matrix = {
      columns: matrixColumns,
      rows: matrixRows,
      pointGroups: matrixPointGroups,
      pointsOnCategoryLevel: this.getMatrixPoints(pointsOnCategoryLevel),
      summary: this.getSummary(),
    };
  }

  private displayDangerAlertMatrixNotAvailable(): void {
    const categoryNumber = this.inspectionCategory.number;
    const categoryName = this.i18nTextPipe.transform(this.inspectionCategory.name);
    this.alerts.danger(
      `${categoryNumber} ${categoryName}: ${this.translateService.instant('MatrixNotAvailable')}`,
      null,
    );
  }

  private getDistinctPointNumbers(pointGroups: InspectionElementDto[]): Array<string> {
    const distinctPointNumbers = new Set<string>();
    pointGroups.forEach((pointGroup) => {
      if (pointGroup) {
        pointGroup.childElements?.forEach((pointGroupChild) => {
          if (
            pointGroupChild.type === InspectionResultElementType.Point &&
            pointGroupChild.number
          ) {
            distinctPointNumbers.add(pointGroupChild.number);
          }
        });
      }
    });
    return Array.from(distinctPointNumbers).sort((a, b) => (a > b ? 1 : -1));
  }

  private getDistinctType1PointGroupNumbers(pointGroups: InspectionElementDto[]): Array<string> {
    const distinctPointGroupNumbers = new Set<string>();
    pointGroups.forEach((pointGroup) => {
      if (pointGroup) {
        pointGroup.childElements?.forEach((pointGroupChild) => {
          if (
            pointGroupChild.type === InspectionResultElementType.PointGroup &&
            pointGroupChild.pointGroupType === PointGroupType.SubheadWithoutDataEntry &&
            pointGroupChild.number
          ) {
            distinctPointGroupNumbers.add(pointGroupChild.number);
          }
        });
      }
    });
    return Array.from(distinctPointGroupNumbers).sort((a, b) => (a > b ? 1 : -1));
  }

  private getMatrixColumns(pointGroups: InspectionElementDto[]): InspectionResultMatrixColumnDto[] {
    return pointGroups
      .filter((pointGroup) => pointGroup.number !== null)
      .map((pointGroup) => ({
        pointGroupNumber: pointGroup.number!,
        pointGroupName: pointGroup.name,
        pointGroupDescription: pointGroup.description,
        pointGroupResult: this.categoryDetail.categoryResult.results?.find(
          (r) => r.inspectionElementKey === pointGroup.key,
        ),
      }));
  }

  private getMatrixPointGroups(
    pointGroups: InspectionElementDto[],
  ): InspectionResultMatrixPointGroupDto[] {
    const matrixPointGroups = new Array<InspectionResultMatrixPointGroupDto>();
    const type1PointGroupNumbers = this.getDistinctType1PointGroupNumbers(pointGroups);
    type1PointGroupNumbers.forEach((row) => {
      matrixPointGroups.push(
        ...pointGroups
          .filter((column) => column.number !== null)
          .map((column) => ({
            columnNumber: column.number!,
            columnName: column.name,
            rowNumber: row,
            rowName: { germanText: row, frenchText: row, italianText: row },
            pointGroupResult: this.getDefaultPointResultModel(),
          })),
      );
    });
    return matrixPointGroups;
  }

  private getMatrixRows(pointGroups: InspectionElementDto[]): InspectionResultMatrixRowDto[] {
    const distinctPointNumbers = this.getDistinctPointNumbers(pointGroups);
    const matrixRows = distinctPointNumbers.map((pointNumber) =>
      this.getMatrixRow(pointGroups, pointNumber),
    );
    const type1PointGroupNumbers = this.getDistinctType1PointGroupNumbers(pointGroups);
    type1PointGroupNumbers.forEach((pointGroupNumber) => {
      matrixRows.push(...this.getMatrixRowsForType1PointGroup(pointGroups, pointGroupNumber));
    });
    return matrixRows;
  }

  private getMatrixRowsForType1PointGroup(
    pointGroups: InspectionElementDto[],
    pointGroupNumber: string,
  ): InspectionResultMatrixRowDto[] {
    const matrixRows = new Array<InspectionResultMatrixRowDto>();
    let type1PointGroupDefinition: InspectionElementDto | null | undefined = null;
    for (let i = 0; i < pointGroups.length && type1PointGroupDefinition == null; i++) {
      type1PointGroupDefinition = pointGroups[i].childElements?.find(
        (childElement) =>
          childElement.number === pointGroupNumber &&
          childElement.pointGroupType === PointGroupType.SubheadWithoutDataEntry,
      );
    }
    if (type1PointGroupDefinition) {
      matrixRows.push({
        isPointGroup: true,
        description: type1PointGroupDefinition.description,
        number: type1PointGroupDefinition.number,
        name: type1PointGroupDefinition.name,
        descriptionDisplayed: false,
        isFieldValuePoint: false,
        pointResultModel: null,
        points: null,
        defectDescriptionSuggestions: null,
        textFieldValueSuggestions: null,
        isTextReadonly: false,
      });
      const type1PointGroups = pointGroups
        .map((pointGroup) =>
          pointGroup.childElements?.find(
            (childElement) =>
              childElement.number === pointGroupNumber &&
              childElement.pointGroupType === PointGroupType.SubheadWithoutDataEntry,
          ),
        )
        .filter((pg): pg is InspectionElementDto => pg !== undefined);
      const distinctPointNumbers = this.getDistinctPointNumbers(type1PointGroups);
      distinctPointNumbers.forEach((pointNumber) => {
        matrixRows.push(
          this.getMatrixRowForType1PointGroups(pointGroups, pointGroupNumber, pointNumber),
        );
      });
    }
    return matrixRows;
  }

  private getMatrixRow(
    pointGroups: InspectionElementDto[],
    pointNumber: string,
  ): InspectionResultMatrixRowDto {
    const point = this.getPointDefinition(pointGroups, pointNumber);
    const rowPoints = pointGroups.map((pointGroup) => {
      const pointInPointGroup = this.getPoint(pointGroup, pointNumber, null);
      let rowPoint: InspectionResultMatrixRowPointDto | null = null;
      if (pointInPointGroup) {
        rowPoint = {
          pointGroupKey: pointGroup.key,
          result: this.categoryDetail.categoryResult.results?.find(
            (r) => r.inspectionElementKey === pointInPointGroup.key,
          ),
          defectDescriptionSuggestions: this.getDefectDescriptionSuggestions(
            pointGroup.number,
            pointInPointGroup.number,
          ),
          textFieldValueSuggestions: this.getTextFieldValueSuggestions(
            pointGroup.number,
            pointInPointGroup.number,
          ),
          isFkp: pointInPointGroup.isFkp,
          displayRemarkOfFieldValue: this.getDisplayRemarkOfFieldValue(
            pointGroup.number,
            pointInPointGroup.number,
          ),
          pointNumber: pointInPointGroup.number,
          pointDescription: pointInPointGroup.description,
          pointDescriptionDisplayed: false,
        };
      } else {
        rowPoint = {
          pointGroupKey: pointGroup.key,
          result: null,
          defectDescriptionSuggestions: null,
          textFieldValueSuggestions: null,
          isFkp: pointGroup.isFkp,
          displayRemarkOfFieldValue: false,
          pointNumber: pointGroup.number,
          pointDescription: pointGroup.description,
          pointDescriptionDisplayed: false,
        };
      }
      return rowPoint;
    });
    return {
      isPointGroup: false,
      name: point?.name,
      description: point?.description,
      descriptionDisplayed: false,
      number: pointNumber,
      pointResultModel: this.getDefaultPointResultModel(),
      isFieldValuePoint: point?.fieldType ? true : false,
      points: rowPoints,
      defectDescriptionSuggestions: this.getDefectDescriptionSuggestions(null, pointNumber),
      textFieldValueSuggestions: this.getTextFieldValueSuggestions(null, pointNumber),
      isTextReadonly: this.getIsTextSetToReadonly(null, pointNumber),
    };
  }

  private getMatrixRowForType1PointGroups(
    pointGroups: InspectionElementDto[],
    type1PointGroupNumber: string,
    pointNumber: string,
  ): InspectionResultMatrixRowDto {
    const type1PointGroups = pointGroups.map((pointGroup) =>
      pointGroup.childElements?.find(
        (childElement) =>
          childElement.number === type1PointGroupNumber &&
          childElement.pointGroupType === PointGroupType.SubheadWithoutDataEntry,
      ),
    );
    const point = this.getPointDefinition(type1PointGroups, pointNumber);
    const rowPoints = pointGroups.map((pointGroup) => {
      const type1PointGroup = pointGroup.childElements?.find(
        (childElement) =>
          childElement.number === type1PointGroupNumber &&
          childElement.pointGroupType === PointGroupType.SubheadWithoutDataEntry,
      );
      let rowPoint: InspectionResultMatrixRowPointDto | null = null;
      if (type1PointGroup) {
        const pointInPointGroup = this.getPoint(pointGroup, pointNumber, type1PointGroupNumber);
        let defectDescriptionSuggestions: I18NText[] | null = null;
        let textFieldValueSuggestions: I18NText[] | null = null;
        if (pointInPointGroup) {
          defectDescriptionSuggestions = this.getDefectDescriptionSuggestions(
            pointGroup.number,
            pointInPointGroup.number,
          );
          textFieldValueSuggestions = this.getTextFieldValueSuggestions(
            pointGroup.number,
            pointInPointGroup.number,
          );
          const displayRemarkOfFieldValue = this.getDisplayRemarkOfFieldValue(
            pointGroup.number,
            pointInPointGroup.number,
          );
          rowPoint = {
            pointGroupKey: pointGroup.key,
            result: this.categoryDetail.categoryResult.results?.find(
              (r) => r.inspectionElementKey === pointInPointGroup.key,
            ),
            defectDescriptionSuggestions,
            textFieldValueSuggestions,
            isFkp: pointInPointGroup.isFkp,
            displayRemarkOfFieldValue,
            pointNumber: pointInPointGroup.number,
            pointDescription: pointInPointGroup.description,
            pointDescriptionDisplayed: false,
          };
        } else {
          rowPoint = {
            pointGroupKey: pointGroup.key,
            result: null,
            defectDescriptionSuggestions,
            textFieldValueSuggestions,
            isFkp: pointGroup.isFkp,
            displayRemarkOfFieldValue: false,
            pointNumber: pointGroup.number,
            pointDescription: pointGroup.description,
            pointDescriptionDisplayed: false,
          };
        }
      } else {
        rowPoint = {
          pointGroupKey: pointGroup.key,
          result: null,
          defectDescriptionSuggestions: null,
          textFieldValueSuggestions: null,
          isFkp: pointGroup.isFkp,
          displayRemarkOfFieldValue: false,
          pointNumber: pointGroup.number,
          pointDescription: pointGroup.description,
          pointDescriptionDisplayed: false,
        };
      }

      return rowPoint;
    });
    return {
      isPointGroup: false,
      name: point?.name,
      description: point?.description,
      descriptionDisplayed: false,
      number: pointNumber,
      pointResultModel: this.getDefaultPointResultModel(),
      isFieldValuePoint: point?.fieldType ? true : false,
      points: rowPoints,
      defectDescriptionSuggestions: this.getDefectDescriptionSuggestions(null, pointNumber),
      textFieldValueSuggestions: this.getTextFieldValueSuggestions(null, pointNumber),
      isTextReadonly: this.getIsTextSetToReadonly(null, pointNumber),
    };
  }

  private getMatrixPoints(points: InspectionElementDto[]): InspectionResultPointDto[] {
    return points.map(
      (point: InspectionElementDto): InspectionResultPointDto => ({
        pointName: point.name,
        pointDescription: point.description,
        pointDescriptionDisplayed: false,
        pointNumber: point.number,
        pointResult: this.categoryDetail.categoryResult.results?.find(
          (r: FlatInspectionResultDto) => r.inspectionElementKey === point.key,
        ),
        defectDescriptionSuggestions: this.getDefectDescriptionSuggestions(null, point.number),
        textFieldValueSuggestions: this.getTextFieldValueSuggestions(null, point.number),
        isFkp: point.isFkp,
        displayRemarkOfFieldValue: this.getDisplayRemarkOfFieldValue(null, point.number),
        previousFieldValue: null,
        isTextReadonly: this.getIsTextSetToReadonly(null, point.number),
      }),
    );
  }

  private getSummary(): InspectionResultSummaryDto {
    return {
      categoryDescription: this.inspectionCategory.description,
      categoryDescriptionDisplayed: false,
      categoryResult: this.categoryDetail.categoryResult.results?.find(
        (r) => r.inspectionElementKey === this.inspectionCategory.key,
      ),
    };
  }

  private getPointDefinition(
    pointGroups: (InspectionElementDto | undefined)[],
    pointNumber: string,
  ): InspectionElementDto | null {
    let point: InspectionElementDto | null = null;
    let pointFound = false;
    let index = 0;
    while (!pointFound && index < pointGroups.length) {
      point =
        this.getPoint(pointGroups[index], pointNumber, pointGroups[index]?.number ?? null) ?? null;
      if (point) {
        pointFound = true;
      }
      index++;
    }

    return point;
  }

  private getPoint(
    pointGroup: InspectionElementDto | undefined,
    pointNumber: string,
    parentPointGroupNumber: string | null,
  ): InspectionElementDto | undefined {
    const pointsOfPointGroup = this.getAllPoints(pointGroup, parentPointGroupNumber);
    return pointsOfPointGroup.find((p) => p.number === pointNumber);
  }

  private getAllPoints(
    pointGroup: InspectionElementDto | null | undefined,
    parentPointGroupNumber: string | null,
  ): InspectionElementDto[] {
    let points = new Array<InspectionElementDto>();
    if (pointGroup?.childElements) {
      points = pointGroup.childElements.filter(
        (pointGroupChild) => pointGroupChild.type === InspectionResultElementType.Point,
      );
      const childPointGroups = pointGroup.childElements.filter(
        (pointGroupChild) =>
          pointGroupChild.type === InspectionResultElementType.PointGroup &&
          (parentPointGroupNumber == null || pointGroupChild.number === parentPointGroupNumber),
      );
      childPointGroups.forEach(
        (childPointGroup) =>
          (points = points.concat(this.getAllPoints(childPointGroup, parentPointGroupNumber))),
      );
    }
    return points;
  }

  private getDefaultPointResultModel(): FlatInspectionResultDto {
    return {
      amountAffected: null,
      complaintRemedyDateDue: null,
      defectDescription: null,
      defectRepetition: null,
      defectSeverity: null,
      // @ts-expect-error: strict null check ignored intentionally
      fieldValue: null,
      // @ts-expect-error: strict null check ignored intentionally
      inspectionElementKey: null,
      inspectionResult: InspectionResultType.NotDefined,
      livestockOwnerFeedbackDateDue: null,
      remark: null,
    };
  }

  private propagateResultsToRowPoints(row: InspectionResultMatrixRowDto) {
    row.points?.forEach((point) => {
      if (point.result && this.isPointDisplayed(point, row)) {
        if (!point.result.fieldValue && row.pointResultModel) {
          point.result.inspectionResult = row.pointResultModel.inspectionResult;
          point.result.remark = row.pointResultModel.remark;
          point.result.defectDescription = row.pointResultModel.defectDescription;
        } else {
          this.resetFieldValue(point.result);
        }
        this.resetPointGroupResult(point.pointGroupKey, point.result);
      }
    });
  }

  private resetPointGroupResult(
    key: string | undefined,
    childResult: FlatInspectionResultDto | null | undefined,
  ) {
    if (!key || !childResult) {
      return;
    }
    const result = this.getResult(key);
    if (
      result &&
      result.inspectionResult !== InspectionResultType.NotDefined &&
      result.inspectionResult !== childResult.inspectionResult
    ) {
      result.inspectionResult = InspectionResultType.NotDefined;
      result.remark = null;
      result.defectDescription = null;
    }
  }

  private resetCategoryResult(childResult: FlatInspectionResultDto | null | undefined) {
    const result = this.getResult(this.inspectionCategory.key);
    const resultChanged = this.setCategoryResultIfAllPointsAreSame(
      result,
      childResult?.inspectionResult,
    );
    if (resultChanged) {
      return;
    }
    if (
      result &&
      this.isResultNotApplicableOrNotControlled(result) &&
      result.inspectionResult !== childResult?.inspectionResult
    ) {
      result.inspectionResult = InspectionResultType.NotDefined;
      result.remark = null;
      result.defectDescription = null;
      result.defectSeverity = null;
    }
  }

  private resetRowResultModel(
    pointNumber: string,
    result: FlatInspectionResultDto | null | undefined,
  ) {
    const row = this.matrix?.rows?.find((r) => r.number === pointNumber);
    if (
      result &&
      row?.pointResultModel &&
      result.inspectionResult !== row.pointResultModel.inspectionResult
    ) {
      row.pointResultModel.inspectionResult = InspectionResultType.NotDefined;
      row.pointResultModel.remark = null;
      row.pointResultModel.defectDescription = null;
    } else if (
      result &&
      row?.pointResultModel &&
      result.inspectionResult === row.pointResultModel.inspectionResult &&
      (result.remark !== row.pointResultModel.remark ||
        result.defectDescription !== row.pointResultModel.defectDescription)
    ) {
      row.pointResultModel.remark = null;
      row.pointResultModel.defectDescription = null;
    }
  }

  private resetAllRowResultModels(): void {
    this.matrix?.rows?.forEach((row) => {
      if (row.pointResultModel && !row.isPointGroup) {
        row.pointResultModel.inspectionResult = InspectionResultType.NotDefined;
        row.pointResultModel.remark = null;
        row.pointResultModel.defectDescription = null;
      }
    });
  }

  private resetAllPointGroupResultModels(): void {
    this.matrix?.pointGroups?.forEach((pointGroup) => {
      if (pointGroup.pointGroupResult) {
        pointGroup.pointGroupResult.inspectionResult = InspectionResultType.NotDefined;
        pointGroup.pointGroupResult.remark = null;
        pointGroup.pointGroupResult.defectDescription = null;
      }
    });
  }

  private isPointDisplayed(
    point: InspectionResultMatrixRowPointDto,
    row: InspectionResultMatrixRowDto,
  ) {
    return this.getPointsToDisplay(row).some(
      (p) => p.result && p.result.inspectionElementKey === point.result?.inspectionElementKey,
    );
  }

  private getPointGroupElement(
    pointGroup: InspectionResultMatrixPointGroupDto,
  ): InspectionElementDto | undefined {
    const categoryPoints = this.getInspectionElement(
      this.inspectionCategory,
      this.inspectionCategory.key,
    );
    const pointGroupElement = categoryPoints?.childElements
      ?.find(
        (column) =>
          column.type === InspectionResultElementType.PointGroup &&
          column.number === pointGroup.columnNumber,
      )
      ?.childElements?.find(
        (r) =>
          r.type === InspectionResultElementType.PointGroup && r.number === pointGroup.rowNumber,
      );
    return pointGroupElement;
  }
}
